import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Form, Input, Button, Table, Space, InputNumber, Popconfirm, Checkbox } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useSnackbar } from 'notistack';
import { getCustomCostItems, addCustomCostItem, updateCustomCostItem, deleteCustomCostItem, getPropertyConfig } from '../apiCalls';
import * as Sentry from "@sentry/react";
import { useParams } from 'react-router-dom';
import PageLayout from './PageLayout';
import { usePageLayout } from '../hooks/usePageLayout';
import { getCurrencySymbol } from '../utils/currencyUtils';

const CustomCostItemsModal = () => {
    const { propertyID } = useParams();
    const { t } = useTranslation();
    const pageLayoutProps = usePageLayout();
    const [form] = Form.useForm();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isAddingNew, setIsAddingNew] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [propertyConfig, setPropertyConfig] = useState(null);
    const { enqueueSnackbar } = useSnackbar();

    useEffect(() => {
        if (propertyID) {
            fetchItems();
            fetchPropertyConfig();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [propertyID]);

    const fetchPropertyConfig = async () => {
        try {
            const config = await getPropertyConfig(propertyID);
            setPropertyConfig(config);
        } catch (err) {
            Sentry.captureException(err);
        }
    };

    const fetchItems = async () => {
        try {
            const data = await getCustomCostItems(propertyID);
            setItems(data);
        } catch (err) {
            enqueueSnackbar(t('customCosts.error.loading'), { variant: 'error' });
            Sentry.captureException(err);
        }
    };

    const handleAddNew = () => {
        form.resetFields();
        setIsAddingNew(true);
        setEditingId(null);
    };

    const handleEdit = (item) => {
        form.setFieldsValue({
            name: item.name,
            description: item.description,
            cost: item.cost,
            includeInClientResponse: item.includeInClientResponse || false
        });
        setIsAddingNew(true);
        setEditingId(item._id);
    };

    const handleSubmit = async (values) => {
        setLoading(true);
        try {
            if (editingId) {
                await updateCustomCostItem(editingId, values.name, values.description, values.cost, values.includeInClientResponse);
                enqueueSnackbar(t('customCosts.success.updated'), { variant: 'success' });
            } else {
                await addCustomCostItem(propertyID, values.name, values.description, values.cost, values.includeInClientResponse);
                enqueueSnackbar(t('customCosts.success.added'), { variant: 'success' });
            }
            setIsAddingNew(false);
            setEditingId(null);
            form.resetFields();
            await fetchItems();
        } catch (err) {
            enqueueSnackbar(err.response?.data || t('customCosts.error.saving'), { variant: 'error' });
            Sentry.captureException(err);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        try {
            await deleteCustomCostItem(id);
            enqueueSnackbar(t('customCosts.success.deleted'), { variant: 'success' });
            await fetchItems();
        } catch (err) {
            enqueueSnackbar(t('customCosts.error.deleting'), { variant: 'error' });
            Sentry.captureException(err);
        }
    };

    const columns = [
        {
            title: t('customCosts.columns.name'),
            dataIndex: 'name',
            key: 'name',
            width: '25%'
        },
        {
            title: t('customCosts.columns.description'),
            dataIndex: 'description',
            key: 'description',
            width: '30%'
        },
        {
            title: t('customCosts.form.cost', { currency: getCurrencySymbol(propertyConfig?.currency || 'GBP') }),
            dataIndex: 'cost',
            key: 'cost',
            width: '15%',
            render: (cost) => {
                const symbol = getCurrencySymbol(propertyConfig?.currency || 'GBP');
                return cost != null ? `${symbol}${cost.toFixed(2)}` : `${symbol}0.00`;
            }
        },
        {
            title: t('customCosts.columns.showInResponse'),
            dataIndex: 'includeInClientResponse',
            key: 'includeInClientResponse',
            width: '15%',
            render: (includeInClientResponse) => (
                <span>{includeInClientResponse ? t('customCosts.included') : t('customCosts.notIncluded')}</span>
            )
        },
        {
            title: t('common.actions'),
            key: 'actions',
            width: '15%',
            render: (_, record) => (
                <Space>
                    <Button
                        type="primary"
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => handleEdit(record)}
                    />
                    <Popconfirm
                        title={t('customCosts.delete.title')}
                        description={t('customCosts.delete.description')}
                        onConfirm={() => handleDelete(record._id)}
                        okText={t('customCosts.delete.yes')}
                        cancelText={t('customCosts.delete.no')}
                    >
                        <Button danger size="small" icon={<DeleteOutlined />} />
                    </Popconfirm>
                </Space>
            )
        }
    ];

    return (
        <PageLayout
            {...pageLayoutProps}
            property={propertyID}
        >
            <div style={{ maxWidth: 1000, margin: '0 auto' }}>
                {!isAddingNew ? (
                    <div>
                        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <h3 style={{ margin: 0 }}>{t('customCosts.title')}</h3>
                            <Button type="primary" icon={<PlusOutlined />} onClick={handleAddNew}>
                                {t('customCosts.addNew')}
                            </Button>
                        </div>
                        <Table
                            columns={columns}
                            dataSource={items}
                            rowKey="_id"
                            pagination={false}
                            loading={loading}
                            scroll={{ x: 'max-content' }}
                        />
                    </div>
                ) : (
                    <Form form={form} onFinish={handleSubmit} layout="vertical" style={{ marginTop: 20 }}>
                        <Form.Item
                            label={t('customCosts.form.name')}
                            name="name"
                            required
                            rules={[{ required: true, message: t('customCosts.validation.nameRequired') }]}
                        >
                            <Input placeholder={t('customCosts.form.namePlaceholder')} maxLength={100} />
                        </Form.Item>

                        <Form.Item label={t('customCosts.form.description')} name="description">
                            <Input.TextArea placeholder={t('customCosts.form.descriptionPlaceholder')} rows={3} maxLength={500} />
                        </Form.Item>

                        <Form.Item
                            label={t('customCosts.form.cost', { currency: getCurrencySymbol(propertyConfig?.currency || 'GBP') })}
                            name="cost"
                            required
                            rules={[
                                { required: true, message: t('customCosts.validation.costRequired') },
                                { type: 'number', min: 0, message: t('customCosts.validation.costPositive') }
                            ]}
                        >
                            <InputNumber min={0} precision={2} placeholder={t('customCosts.form.costPlaceholder')} style={{ width: '100%' }} />
                        </Form.Item>

                        <Form.Item
                            label={t('customCosts.form.showInResponse')}
                            name="includeInClientResponse"
                            valuePropName="checked"
                        >
                            <Checkbox>
                                {t('customCosts.form.showInResponseDesc')}
                            </Checkbox>
                        </Form.Item>

                        <Form.Item>
                            <Space>
                                <Button type="primary" htmlType="submit" loading={loading}>
                                    {editingId ? t('customCosts.buttons.update') : t('customCosts.buttons.add')}
                                </Button>
                                <Button
                                    onClick={() => {
                                        setIsAddingNew(false);
                                        setEditingId(null);
                                        form.resetFields();
                                    }}
                                >
                                    {t('customCosts.buttons.cancel')}
                                </Button>
                            </Space>
                        </Form.Item>
                    </Form>
                )}
            </div>
        </PageLayout>
    );
};

export default CustomCostItemsModal;
